/**
 * i18n.ts — WEB.4.P1.5 — minimal i18n runtime.
 *
 * Loads en + th UI dictionaries at module load (Vite imports the JSON), exposes
 * a `t(key, vars?)` helper that:
 *   - looks up the key in the active language dictionary
 *   - falls back to en when the key is missing in the active dict
 *   - returns the key itself if not found in either (loud failure, not silent)
 *   - interpolates `{{varName}}` placeholders from `vars`
 *
 * Active language is stored in store.lang (persisted to localStorage on
 * change). React components read via `useI18n()` which subscribes to the
 * store slice so changes propagate via Zustand's normal subscription path.
 */

import enUi from "../../../../data/i18n/en/ui.json";
import thUi from "../../../../data/i18n/th/ui.json";

export type LangCode = "en" | "th";

// Dictionary is a nested object of strings (dot-paths like "mainMenu.startGame").
// Use unknown + runtime guards so the JSON imports stay loose; the keys/values
// we access are validated at call time.
type Dict = Record<string, unknown>;

const DICTS: Record<LangCode, Dict> = {
  en: enUi as unknown as Dict,
  th: thUi as unknown as Dict,
};

/** Resolve a dot-path key against a nested dict; returns string or undefined. */
function _lookup(dict: Dict, key: string): string | undefined {
  const parts = key.split(".");
  let cur: unknown = dict;
  for (const part of parts) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return typeof cur === "string" ? cur : undefined;
}

/** Interpolate `{{var}}` placeholders from vars map. */
function _interpolate(s: string, vars: Record<string, string | number> | undefined): string {
  if (vars === undefined) return s;
  return s.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => {
    const v = vars[name];
    return v === undefined ? `{{${name}}}` : String(v);
  });
}

/**
 * Translate a dot-path key in the given language with optional interpolation.
 * Falls back to English when missing in the active dict; returns the raw key
 * if neither dict has it (so missing keys are visible during dev).
 */
export function translate(
  lang: LangCode,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const primary = _lookup(DICTS[lang], key);
  if (primary !== undefined) return _interpolate(primary, vars);
  // Fallback chain: every non-en lang falls back to en.
  if (lang !== "en") {
    const fallback = _lookup(DICTS.en, key);
    if (fallback !== undefined) return _interpolate(fallback, vars);
  }
  return key;
}

/** Local-storage key for persisting the user's language choice. */
export const LANG_STORAGE_KEY = "gdg-lang";

/** Returns the persisted language or "en" if none / invalid. */
export function loadPersistedLang(): LangCode {
  if (typeof window === "undefined") return "en";
  try {
    const v = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (v === "en" || v === "th") return v;
  } catch {
    // localStorage may throw in private mode / SSR.
  }
  return "en";
}

/** Persist the language choice (best-effort; swallows errors). */
export function persistLang(lang: LangCode): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // ignore
  }
}
